import { API_BASE } from './config'
import { getAuthHeaders } from './api'

interface RoomsRequestInit {
  method?: string
  body?: FormData | string
}
import {
  ClientMessage,
  FocusedMinutesRow,
  JoinResult,
  JoinStatusResult,
  MyRoomResult,
  PomodoroPresetKey,
  Room,
  RoomMessage,
  RoomWithName,
  ServerMessage
} from '@/types/rooms'

async function roomsFetch<T>(path: string, init?: RoomsRequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: init?.method,
    body: init?.body,
    headers: {
      ...getAuthHeaders(),
      ...(init?.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {})
    }
  })
  const result = await response.json().catch(() => null)
  if (!response.ok || !result?.success) {
    throw new Error(result?.error?.message || 'İşlem başarısız oldu.')
  }
  return result.data as T
}

export const roomService = {
  list: (): Promise<RoomWithName[]> => roomsFetch<RoomWithName[]>('/rooms'),

  listSite: (): Promise<RoomWithName[]> => roomsFetch<RoomWithName[]>('/rooms/site'),

  get: (code: string): Promise<Room> => roomsFetch<Room>(`/rooms/${code}`),

  mine: (): Promise<MyRoomResult> => roomsFetch<MyRoomResult>('/rooms/mine'),

  create: (input: {
    title: string
    subject?: string | null
    pomodoro_preset?: PomodoroPresetKey
    join_policy?: string
  }): Promise<{ room: Room }> =>
    roomsFetch<{ room: Room }>('/rooms', {
      method: 'POST',
      body: JSON.stringify(input)
    }),

  join: (code: string): Promise<JoinResult> =>
    roomsFetch<JoinResult>(`/rooms/${code}/join`, { method: 'POST' }),

  joinStatus: (code: string): Promise<JoinStatusResult> =>
    roomsFetch<JoinStatusResult>(`/rooms/${code}/join-status`),

  leave: (code: string): Promise<{ left: boolean }> =>
    roomsFetch<{ left: boolean }>(`/rooms/${code}/leave`, { method: 'POST' }),

  kick: (code: string, userId: number): Promise<{ done: boolean }> =>
    roomsFetch<{ done: boolean }>(`/rooms/${code}/kick`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    }),

  ban: (code: string, userId: number, reason?: string): Promise<{ done: boolean }> =>
    roomsFetch<{ done: boolean }>(`/rooms/${code}/ban`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, reason })
    }),

  approve: (code: string, userId: number): Promise<{ done: boolean }> =>
    roomsFetch<{ done: boolean }>(`/rooms/${code}/approve`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    }),

  deny: (code: string, userId: number): Promise<{ done: boolean }> =>
    roomsFetch<{ done: boolean }>(`/rooms/${code}/deny`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    }),

  queueRequest: (code: string, url: string): Promise<{ ok: boolean }> =>
    roomsFetch<{ ok: boolean }>(`/rooms/${code}/queue`, {
      method: 'POST',
      body: JSON.stringify({ action: 'request', url })
    }),

  queueApprove: (code: string, id: string): Promise<{ ok: boolean }> =>
    roomsFetch<{ ok: boolean }>(`/rooms/${code}/queue`, {
      method: 'POST',
      body: JSON.stringify({ action: 'approve', id })
    }),

  queueReject: (code: string, id: string): Promise<{ ok: boolean }> =>
    roomsFetch<{ ok: boolean }>(`/rooms/${code}/queue`, {
      method: 'POST',
      body: JSON.stringify({ action: 'reject', id })
    }),

  queueWithdraw: (code: string, id: string): Promise<{ ok: boolean }> =>
    roomsFetch<{ ok: boolean }>(`/rooms/${code}/queue`, {
      method: 'POST',
      body: JSON.stringify({ action: 'withdraw', id })
    }),

  report: (code: string, input: { reported_user_id?: number; message_id?: number; reason: string }): Promise<{ reported: boolean }> =>
    roomsFetch<{ reported: boolean }>(`/rooms/${code}/report`, {
      method: 'POST',
      body: JSON.stringify(input)
    }),

  sendClip: (code: string, kind: 'voice' | 'video' | 'photo' | 'file', file: File): Promise<{ message: RoomMessage }> => {
    const formData = new FormData()
    formData.append('kind', kind)
    formData.append('file', file)
    return roomsFetch<{ message: RoomMessage }>(`/rooms/${code}/messages`, {
      method: 'POST',
      body: formData
    })
  },

  uploadHostCam: (code: string, file: Blob): Promise<{ r2_key: string; url: string }> => {
    const formData = new FormData()
    formData.append('file', file, 'still.jpg')
    return roomsFetch<{ r2_key: string; url: string }>(`/rooms/${code}/host-cam`, {
      method: 'POST',
      body: formData
    })
  },

  focusedMinutes: (days = 7): Promise<{ days: number; leaderboard: FocusedMinutesRow[]; me: { focused_seconds: number; session_count: number } }> =>
    roomsFetch(`/rooms/focused-minutes?days=${days}`)
}

function toAbsoluteWsUrl(url: string): string {
  if (url.startsWith('ws://') || url.startsWith('wss://')) return url
  const base = new URL(API_BASE)
  const scheme = base.protocol === 'https:' ? 'wss' : 'ws'
  return `${scheme}://${base.host}${url}`
}

export class RoomSocket {
  private ws: WebSocket | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null

  onMessage: (msg: ServerMessage) => void = () => undefined
  onOpen: () => void = () => undefined
  onClose: () => void = () => undefined

  connect(url: string): void {
    this.close()
    this.ws = new WebSocket(toAbsoluteWsUrl(url))
    this.ws.onopen = () => {
      this.startPing()
      this.onOpen()
    }
    this.ws.onmessage = (event) => {
      try {
        this.onMessage(JSON.parse(event.data as string) as ServerMessage)
      } catch {
        return
      }
    }
    this.ws.onclose = () => {
      this.stopPing()
      this.onClose()
    }
    this.ws.onerror = () => {
      this.ws?.close()
    }
  }

  send(message: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message))
    }
  }

  private startPing(): void {
    this.stopPing()
    this.pingTimer = setInterval(() => this.send({ type: 'ping' }), 25000)
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer)
      this.pingTimer = null
    }
  }

  close(): void {
    this.stopPing()
    if (this.ws) {
      this.ws.onclose = null
      this.ws.onmessage = null
      this.ws.onerror = null
      try {
        this.ws.close()
      } catch {
        return
      }
      this.ws = null
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }
}
